import { useEffect, useRef, useState } from 'preact/hooks';
import KnowYourCustomerSkeleton from './KnowYourCustomerSkeleton';
import KnowYourCustomerView from './KnowYourCustomerView';
import { renderIf } from '../../utils/render.utils';
import OnboardingExitModal from '../../components/Onboarding/OnboardingExitModal/OnboardingExitModal';
import LoadingScreen from '../../components/shared/LoadingScreen/LoadingScreen';
import useAndroidBackPress from '../../hooks/back.listener.hook';
import NavigationUtils from '../../utils/navigation.utils';
import OnboardingService from '../../services/onboarding.service';
import OnboardingStatusUrlUtils, {
  UserApplicationReason,
  UserApplicationStatus,
  UserIdentityStatus,
} from '../../utils/onboarding-status-url.utils';
import { DeeplinkEvent } from '../../sdk/deeplink.event';
import LuckyDeeplink from '../../constants/lucky.deeplink';
import DeviceUtils from '../../utils/device.utils';
import SettingsService from '../../services/settings.service';

export default function KnowYourCustomerPage({ popup, organization }) {
  const [loadingScreen] = useState(false);

  const closeRef = useRef(null);

  const showExitPopup = () => {
    NavigationUtils.navigate(NavigationUtils.Routes.KYC, false, {
      popup: KnowYourCustomerView.PopupType.EXIT,
    });
  };

  useAndroidBackPress(closeRef.current, () => {
    if (window.location.search.includes('popup')) {
      history.back();
    } else {
      closeRef.current.props.onClick();
    }
  });

  useEffect(() => {
    if (!organization) {
      return NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
    }

    Promise.all([
      SettingsService.getOnboarding(organization).catch(e => {
        console.error(
          `Something went wrong during settings onboarding fetch. ${e}`,
        );
      }),
      OnboardingService.getStatus(organization),
    ])
      .then(([settingsResponse, statusResponse]) => {
        const { data: settings } = settingsResponse;
        const { data } = statusResponse;

        // ATTENTION: if you want to send data in body from android, you must create a base64 param query string in the depplink.
        // ATTENTION: iOS will send everything you have in the query string.
        const isAndroid = DeviceUtils.isAndroid();

        if (data.needsOcr) {
          const params = isAndroid
            ? `params=${btoa(`organizationCode=${organization}`)}`
            : `organizationCode=${organization}`;

          DeeplinkEvent.open(`${LuckyDeeplink.OCR}&${params}`);
          return;
        }

        if (data.needsOnboarding) {
          console.info(`User needs onboarding. Redirecting to Onboarding Page`);
          return NavigationUtils.navigate(NavigationUtils.Routes.ONBOARDING);
        }

        const userApplication = (data.userApplications || []).find(
          userApplication => userApplication.organizationCode === organization,
        );

        const credit =
          settings?.lendingButtonEnabled && data?.credit ? data?.credit : {};

        const url = OnboardingStatusUrlUtils.getUrl(
          data.userIdentity,
          userApplication,
          credit,
          settings?.automaticLendingEnabled,
          settings?.personalizedCardEnabled,
        );

        const params = OnboardingStatusUrlUtils.getParams({
          credit,
          organizationCode: organization,
          userApplication,
          automaticLendingEnabled: settings?.automaticLendingEnabled,
        });

        NavigationUtils.navigate(url, false, params);
      })
      .catch(err => {
        console.error(`Something went wrong`, err);
        if (err?.body?.code === 'USER_NOT_INVITED_ERROR') {
          const url = OnboardingStatusUrlUtils.getUrl(
            {},
            {
              status: UserApplicationStatus.REJECTED,
              reason: UserApplicationReason.INFORMATION_MISMATCH,
            },
          );
          return NavigationUtils.navigate(url, false);
        }

        NavigationUtils.navigate(NavigationUtils.Routes.TRY_AGAIN);
      });
  }, []);

  return (
    <>
      <KnowYourCustomerSkeleton
        onCloseClick={showExitPopup}
        closeRef={closeRef}
      />

      <OnboardingExitModal
        showing={!!popup && popup === KnowYourCustomerView.PopupType.EXIT}
        onCloseClick={() => {
          NavigationUtils.navigate(NavigationUtils.Routes.KYC, true);
        }}
      />

      {renderIf(loadingScreen, <LoadingScreen />)}
    </>
  );
}
